import ZoomMode from "../settings/ZoomMode";
import MouseMode from "../settings/MouseMode";
import AbstractViewer, {AbstractViewerProperties, AbstractViewerState, VIEWER_PADDING} from "../AbstractViewer";
import React, {createRef, ReactNode, RefObject} from "react";
import DomHelper from "../../../shared/helper/DomHelper";
import PortalDocument from "../../document/PortalDocument";
import DocumentSettings from "../../document/DocumentSettings";
import LengthUnit from "../../../shared/units/LengthUnit";
import {ViewerMode} from "../ViewerMode";
import NumberHelper from "../../../shared/helper/NumberHelper";
import Exception from "../../../shared/exception/Exception";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {MessageType} from "../../../shared/log/MessageInterface";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

export interface ImageViewerProperties extends AbstractViewerProperties {
	zoomValue: number;
	zoomMode: ZoomMode;
}

export interface ImageViewerState extends AbstractViewerState {
	loading: boolean;
	viewerData: string;
	error?: string;
	width: number;
	height: number;
}

export class ImageViewerDefinition extends AbstractViewer<ImageViewerProperties, ImageViewerState> {
	private loaderElement: RefObject<HTMLDivElement>;
	private imageElement: RefObject<HTMLImageElement>;
	private resizeObserver: ResizeObserver;

	public static defaults(): ImageViewerProperties {
		return {
			viewerMode: ViewerMode.DEFAULT,
			mouseMode: MouseMode.NONE,
			zoomMode: ZoomMode.PAGE,
			zoomValue: 100
		} as ImageViewerProperties;
	}

	constructor(props: Readonly<ImageViewerProperties>) {
		super(props);

		this.state = {
			...this.state,
			loading: true,
			width: 150,
			height: 150
		};

		this.updateZoom = this.updateZoom.bind(this);

		this.loaderElement = createRef<HTMLDivElement>();
		this.imageElement = createRef<HTMLImageElement>();
		this.resizeObserver = new ResizeObserver(this.updateZoom);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		if (this.loaderElement.current !== null) {
			// Add active class after loader is mounted so the fade animation is applied
			DomHelper.addClass(this.loaderElement.current, "active");
		}

		try {
			await super.componentDidMount();
			await this.updateViewerData();
		} catch (e: any) {
			let error: Exception = e;

			this.setState({
				loading: false,
				error: error.message
			});
			return;
		}

		this.setState({
			loading: false,
			error: undefined
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		this.setState({
			loading: true,
			error: undefined
		});

		await super.componentWillUnmount();

		if (this.viewerElement.current !== null) {
			this.resizeObserver.unobserve(this.viewerElement.current);
		}
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<ImageViewerProperties>, prevState: Readonly<ImageViewerState>
	): Promise<void> {
		if (this.state.loading) {
			return;
		}

		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.attachmentKey !== prevProps.attachmentKey
		];

		if (documentChanged.includes(true)) {
			this.setState({
				loading: true,
				error: undefined
			});

			try {
				await super.componentWillUnmount();
				await super.componentDidMount();
				await this.updateViewerData();
				this.updateZoom();
			} catch (e: any) {
				let error: Exception = e;

				this.setState({
					loading: false,
					error: error.message
				});
				return;
			}

			this.setState({
				loading: false,
				error: undefined
			});
			return;
		}

		if (this.state.loading !== prevState.loading && this.viewerElement.current !== null) {
			this.resizeObserver.observe(this.viewerElement.current);
		}

		let changeConditions: Array<boolean> = [
			this.state.loading !== prevState.loading,
			this.props.zoomMode !== prevProps.zoomMode,
			this.props.zoomValue !== prevProps.zoomValue
		];

		if (changeConditions.includes(true)) {
			await this.updateZoom();
		}
	}

	private async updateViewerData(): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let viewerData: string = "";
		if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
			this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment | undefined =
				portalDocument.getAttachment(FunctionHelper.applyValue(this.props.attachmentKey, ""));

			if (FunctionHelper.exists(attachment)) {
				viewerData = await portalDocument.loadViewerData(attachment);
			}
		} else {
			viewerData = await portalDocument.loadViewerData();
		}

		this.setState({
			viewerData: viewerData
		});
	}

	private updateZoom(): void {
		if (!FunctionHelper.exists(this.viewerElement.current) || !FunctionHelper.exists(this.imageElement.current)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (!portalDocument.container.loaded) {
			return;
		}

		let width: number = FunctionHelper.applyValue(this.imageElement.current!.naturalWidth, 150);
		let height: number = FunctionHelper.applyValue(this.imageElement.current!.naturalHeight, 150);

		let viewerRect: DOMRect = this.viewerElement.current!.getBoundingClientRect();

		let boundaryWidth: number = viewerRect.width;
		boundaryWidth -=
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
				VIEWER_PADDING : 0;
		boundaryWidth -=
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
				VIEWER_PADDING + DomHelper.SCROLLBAR_SIZE : 0;
		boundaryWidth = Math.max(boundaryWidth, 0);

		let boundaryHeight: number = viewerRect.height;
		boundaryHeight -=
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
				VIEWER_PADDING : 0;
		boundaryHeight -=
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
				VIEWER_PADDING + DomHelper.SCROLLBAR_SIZE : 0;
		boundaryHeight = Math.max(boundaryHeight, 0);

		let computedScaleHeight: number = boundaryHeight / height;
		let computedScaleWidth: number = boundaryWidth / width;
		let scale: number = 1;

		switch (this.props.zoomMode) {
			case ZoomMode.CUSTOM:
				scale = this.props.zoomValue / 100;
				break;
			case ZoomMode.HEIGHT:
				scale = computedScaleHeight;
				break;
			case ZoomMode.WIDTH:
				scale = computedScaleWidth;
				break;
			case ZoomMode.PAGE:
				scale = computedScaleHeight >= computedScaleWidth ? computedScaleWidth : computedScaleHeight;
				break;
			case ZoomMode.ORIGINAL:
				scale = 1;
				break;
		}

		if (this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) {
			scale = NumberHelper.parseInt(scale * 100, {min: 10, max: 400}) / 100;
		}

		let zoomValue: number = Math.floor(scale * 100);

		this.setState({
			width: width * scale,
			height: height * scale
		});

		if ((this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
			this.props.zoomMode !== ZoomMode.CUSTOM && zoomValue !== this.props.zoomValue) {
			documentSettings.activeViewer.zoomValue = zoomValue;
			this.props.context.documentManager.updateGlobalDocumentList();
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.loading) {
			return (
				<div className="viewer image" ref={this.viewerElement}>
					<div className="loader" ref={this.loaderElement}>
						<span className="icon icon-spinner spin"/>
					</div>
				</div>
			);
		}

		if (FunctionHelper.exists(this.state.error)) {
			return (
				<div className="viewer image" ref={this.viewerElement}>
					{
						(
							this.props.viewerMode === ViewerMode.PREVIEW ||
							this.props.viewerMode === ViewerMode.ATTACHMENT ||
							this.props.viewerMode === ViewerMode.PORTFOLIO ||
							this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE ||
							this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW
						) &&
						<div className="fallback-icon">
							<span className="icon icon-image"/>
						</div>
					}
					{
						this.props.viewerMode !== ViewerMode.PREVIEW &&
						this.props.viewerMode !== ViewerMode.ATTACHMENT &&
						this.props.viewerMode !== ViewerMode.PORTFOLIO &&
						this.props.viewerMode !== ViewerMode.PORTFOLIO_PAGE &&
						this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
						<MessageBox type={MessageType.DANGER} language={i18next.language}>
							{this.state.error}
						</MessageBox>
					}
				</div>
			);
		}

		let viewerClasses: string = "image-viewer";
		if (this.state.moveActive) {
			viewerClasses += " moveing";
		} else {
			viewerClasses += " move";
		}

		return (
			<div className="viewer image" ref={this.viewerElement} onWheel={this.handleMouseWheel}
				 onTouchStart={this.onTouchStart} onTouchMove={this.onTouchMove} onTouchEnd={this.onTouchEnd}>
				<div className={viewerClasses} onPointerDown={
					(event) => this.handleMouseDown(event.nativeEvent)
				}>
					<div className="image-group">
						<img alt="Viewer" src={this.state.viewerData} ref={this.imageElement} style={{
							width: this.state.width + LengthUnit.PIXEL,
							height: this.state.height + LengthUnit.PIXEL
						}}/>
					</div>
				</div>
			</div>
		);
	}
}

const ImageViewer = React.memo(ImageViewerDefinition,
	(prevProps: Readonly<ImageViewerProperties>, nextProps: Readonly<ImageViewerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.zoomMode !== prevProps.zoomMode) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.viewerMode !== prevProps.viewerMode) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		return true;
	}
);

export default ImageViewer;